import { useEffect, useState, useSyncExternalStore } from 'react'
import { BotaoIcone } from './BotaoIcone'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { EVENTOS, type RespostaDeApoioDto, type UsuarioSessao } from '@crm/shared'
import { CHAVE_PEDIDOS_DE_APOIO, usePedidosDeApoio } from '../api/hooks-chat'
import { RespostaAoPedido } from '../paginas/bate-papo/Apoio'
import { pode } from '../permissao'
import { pegarSocket, soltarSocket } from '../tempo-real/socket'
import { juntar } from './ui'

/**
 * O pedido de apoio chega sem interromper ninguém (ADR 0043).
 *
 * Desenho aprovado em 05/10/2026: quem pede não espera; quem foi chamado vê
 * um cartão no canto da tela, que não cobre o trabalho, com quem pediu, o
 * cliente, o pedido, um comentário e os botões Aceitar e Recusar. Depois da
 * escolha, um aviso rápido com a decisão e o nome do cliente aparece para os
 * dois.
 *
 * Pedido de 06/10/2026: o cartão fica até a pessoa responder (o "Depois",
 * que escondia o cartão na sessão, saiu), e com mais de um pedido aparece um
 * de cada vez, com o contador "1 de 4" e as setas para ver os outros. Antes
 * eram até três cartões empilhados, que cobriam metade da altura da tela.
 */

const AVISO_DURA_MS = 6000

interface AvisoDeResposta extends RespostaDeApoioDto {
  id: number
}

/*
  Os avisos de resposta moram fora do componente: chegam pelo socket (ADR 0043).
*/
let avisosNaTela: AvisoDeResposta[] = []
const ouvintesDosAvisos = new Set<() => void>()

function publicarAvisos(novos: AvisoDeResposta[]) {
  avisosNaTela = novos
  for (const ouvinte of ouvintesDosAvisos) ouvinte()
}

function assinarAvisos(ouvinte: () => void) {
  ouvintesDosAvisos.add(ouvinte)
  return () => ouvintesDosAvisos.delete(ouvinte)
}

/**
 * Mostra o aviso rápido da resposta a um pedido de apoio. Devolve como tirar
 * antes da hora; com `duracaoMs` nulo, ele fica até ser tirado.
 */
export function avisarRespostaDeApoio(
  resposta: RespostaDeApoioDto,
  duracaoMs: number | null = AVISO_DURA_MS,
): () => void {
  const id = Date.now() + Math.random()
  publicarAvisos([...avisosNaTela, { ...resposta, id }])
  const tirar = () => publicarAvisos(avisosNaTela.filter((a) => a.id !== id))
  if (duracaoMs !== null) window.setTimeout(tirar, duracaoMs)
  return tirar
}

function fraseDaResposta(r: RespostaDeApoioDto): string {
  const verbo = r.aceitou ? 'aceitou' : 'recusou'
  const comentario = r.comentario ? `: "${r.comentario}"` : '.'
  return r.paraQuemPediu
    ? `${r.quemRespondeuNome} ${verbo} o apoio no cliente ${r.clienteNome}${comentario}`
    : `Você ${verbo} o apoio no cliente ${r.clienteNome}.`
}

export function PedidosDeApoio({
  usuario,
  canto,
}: {
  usuario: UsuarioSessao
  /** A pilha do canto de baixo, à esquerda, que o Layout monta. */
  canto: HTMLElement | null
}) {
  const cliente = useQueryClient()
  const navegar = useNavigate()
  const { data: pedidos } = usePedidosDeApoio(pode(usuario, 'bate_papo.ver'))
  const [indice, setIndice] = useState(0)
  const avisos = useSyncExternalStore(assinarAvisos, () => avisosNaTela)

  useEffect(() => {
    const socket = pegarSocket()
    const recarregar = () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_PEDIDOS_DE_APOIO })
      void cliente.invalidateQueries({ queryKey: ['conversas'] })
    }
    const aoResponder = (dados: unknown) => {
      avisarRespostaDeApoio(dados as RespostaDeApoioDto)
      recarregar()
    }
    socket.on(EVENTOS.apoioPedido, recarregar)
    socket.on(EVENTOS.apoioAtualizado, recarregar)
    socket.on(EVENTOS.apoioRespondido, aoResponder)
    return () => {
      socket.off(EVENTOS.apoioPedido, recarregar)
      socket.off(EVENTOS.apoioAtualizado, recarregar)
      socket.off(EVENTOS.apoioRespondido, aoResponder)
      soltarSocket()
    }
  }, [cliente])

  const lista = pedidos ?? []
  // Respondido um, o próximo toma o lugar; a posição nunca passa do fim da lista.
  const atual = Math.min(indice, Math.max(lista.length - 1, 0))
  const pedido = lista[atual] ?? null
  if (!canto || (!pedido && avisos.length === 0)) return null

  return createPortal(
    <>
      {avisos.map((aviso) => (
        <div
          key={aviso.id}
          role="status"
          className={juntar(
            'aviso-entrando pointer-events-auto rounded-2xl px-4 py-3 text-sm shadow-alta ring-1',
            aviso.aceitou
              ? 'bg-green-50 text-green-800 ring-green-200'
              : 'bg-red-50 text-red-800 ring-red-200',
          )}
        >
          {fraseDaResposta(aviso)}
        </div>
      ))}

      {pedido ? (
        <section
          key={pedido.apoioId}
          aria-label={`Pedido de apoio de ${pedido.deNome}`}
          className="aviso-entrando pointer-events-auto rounded-2xl bg-superficie p-4 shadow-alta ring-1 ring-amber-300"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-tinta-900">{pedido.deNome} pediu seu apoio</p>
            {lista.length > 1 ? (
              <div
                className="-mr-1 -mt-1 flex shrink-0 items-center"
              >
                <BotaoIcone
                  rotulo="Pedido anterior"
                  pequeno
                  disabled={atual === 0}
                  onClick={() => setIndice(atual - 1)}
                >
                  <span aria-hidden="true">‹</span>
                </BotaoIcone>
                <span className="px-0.5 text-xs tabular-nums text-tinta-500">
                  {atual + 1} de {lista.length}
                </span>
                <BotaoIcone
                  rotulo="Próximo pedido"
                  pequeno
                  dicaAlinhada="direita"
                  disabled={atual >= lista.length - 1}
                  onClick={() => setIndice(atual + 1)}
                >
                  <span aria-hidden="true">›</span>
                </BotaoIcone>
              </div>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-tinta-600">
            Cliente{' '}
            <Link
              to={`/bate-papo?chamado=${pedido.chamadoId}`}
              className="font-medium text-marca hover:underline"
            >
              {pedido.clienteNome}
            </Link>
          </p>
          {pedido.comentario ? (
            <p className="mt-1 text-sm text-tinta-700">"{pedido.comentario}"</p>
          ) : null}
          <div className="mt-3">
            <RespostaAoPedido
              chamadoId={pedido.chamadoId}
              apoioId={pedido.apoioId}
              /*
                Aceitou: a conversa abre na hora (mapa de uso, 08/10/2026).
                Antes a tela ficava onde estava, e a pessoa tinha de procurar o
                cliente em Minhas.
              */
              aoResponder={(aceitou) => {
                if (aceitou) {
                  navegar(`/bate-papo?chamado=${pedido.chamadoId}`)
                }
              }}
            />
          </div>
        </section>
      ) : null}
    </>,
    canto,
  )
}
